import React, { useState, useEffect } from "react";
import { Link } from "react-scroll";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import Image from "next/image";

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking a link
  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: "Home", to: "hero" },
    { name: "About", to: "about" },
    { name: "Projects", to: "projects" },
    { name: "Skills", to: "skills" },
    { name: "Experience", to: "experience" },
    { name: "Education", to: "education" },
    { name: "Contact", to: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 dark:bg-portfolio-dark/90 backdrop-blur-sm shadow-md py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="container-custom flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img src="/logo.png" alt="Logo" width={42} height={42} />
          <Link
            to="hero"
            spy={true}
            smooth={true}
            duration={500}
            className="text-xl font-bold text-portfolio-blue cursor-pointer z-50"
            onClick={handleLinkClick}
          ></Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
              activeClass="text-portfolio-blue font-semibold"
              className="text-gray-600 hover:text-portfolio-blue transition-colors duration-300 cursor-pointer text-sm lg:text-base"
              onClick={handleLinkClick}
            >
              {item.name}
            </Link>
          ))}
          <Button
            className="button-primary text-sm lg:text-base"
            onClick={() => {
              const element = document.createElement("a");
              element.setAttribute("href", "#");
              element.setAttribute("download", "anas-ch.pdf");
              element.style.display = "none";
              document.body.appendChild(element);
              element.click();
              document.body.removeChild(element);
            }}
          >
            Resume
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden z-50 p-2 text-gray-600 hover:text-portfolio-blue transition-colors"
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-white/95 dark:bg-portfolio-dark/95 backdrop-blur-sm transition-transform duration-300 transform md:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="container-custom py-20">
          <div className="flex flex-col items-center space-y-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                spy={true}
                smooth={true}
                offset={-70}
                duration={500}
                className="text-xl font-medium text-gray-800 dark:text-gray-200 hover:text-portfolio-blue transition-colors duration-300"
                onClick={handleLinkClick}
              >
                {item.name}
              </Link>
            ))}
            <Button
              className="button-primary mt-4"
              onClick={() => {
                const element = document.createElement("a");
                element.setAttribute("href", "#");
                element.setAttribute("download", "resume.pdf");
                element.style.display = "none";
                document.body.appendChild(element);
                element.click();
                document.body.removeChild(element);
                setIsMobileMenuOpen(false);
              }}
            >
              Resume
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
